// Registered Printers section for Settings -> Label Printing tab.
// Printers stored in config_settings: printer_<name> = MAC (or empty), printer_<name>_ip = IP.
// Layout mirrors RegisteredScalesSection: inline add form, card list with IP:9100 display.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Printer, Plus, Trash2 } from 'lucide-react';
import productionService from '../../../services/productionService';
import stationManagementService from '../../../services/stationManagementService';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getPrintServiceUrl } from '../../../services/operatorService';

interface PrinterEntry {
  key: string;       // e.g. "printer_lobby"
  nickname: string;  // e.g. "lobby"
  mac: string;       // MAC address (may be empty)
  ip: string;        // static IP
  stationId: number | string | null;  // number for real stations; 'supervisor_office' string for virtual
  stationName: string | null;
}

interface Station {
  station_id: number;
  station_name: string;
  printer_mac_id: string | null;
}

const RegisteredPrintersSection: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMacField, setShowMacField] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [newIp, setNewIp] = useState('');
  const [newPort, setNewPort] = useState('9100');
  const [newMac, setNewMac] = useState('');
  const [newStationId, setNewStationId] = useState('');
  const [testingKey, setTestingKey] = useState<string | null>(null);

  // Fetch all config settings and build printer entries
  const { data: printers = [], isLoading } = useQuery<PrinterEntry[]>(
    'registered-printers',
    async () => {
      const [all, stns] = await Promise.all([
        productionService.getConfigSettings(),
        stationManagementService.getStationPrinters(),
      ]);

      // Build IP map from companion _ip entries
      const ipMap: Record<string, string> = {};
      all.filter(s => s.setting_key.endsWith('_ip') && s.setting_key.startsWith('printer_'))
        .forEach(s => { ipMap[s.setting_key.replace(/_ip$/, '')] = s.setting_value; });

      // Build printer entries from main printer_* keys
      return all
        .filter(s =>
          s.setting_key.startsWith('printer_') &&
          s.setting_key !== 'printer_subnet' &&
          !s.setting_key.endsWith('_ip')
        )
        .map(s => {
          const stn = stns.find(st => st.printer_mac_id === s.setting_key);
          return {
            key: s.setting_key,
            nickname: s.setting_key.replace(/^printer_/, ''),
            mac: s.setting_value || '',
            ip: ipMap[s.setting_key] || '',
            stationId: stn?.station_id ?? null,
            stationName: stn?.station_name ?? null,
          };
        });
    },
    { refetchOnMount: 'always' }
  );

  // Fetch stations for the add-form dropdown
  const { data: stations = [] } = useQuery<Station[]>(
    'station-printers',
    () => stationManagementService.getStationPrinters(),
    { refetchOnMount: 'always' }
  );

  // Which station IDs already have a printer
  const assignedStationIds = new Set(printers.filter(p => p.stationId).map(p => p.stationId));
  // Check if supervisor office already has a printer assigned
  const supervisorOfficeAssigned = printers.some(p => p.key === 'printer_supervisor_office');

  // Add printer: creates main key + _ip key + assigns station
  const addMutation = useMutation(
    async ({ nickname, ip, port, mac, stationId }: { nickname: string; ip: string; port: string; mac: string; stationId: number | string }) => {
      const key = `printer_${nickname}`;
      await productionService.createConfigSetting(key, mac.toUpperCase() || '', `Zebra printer: ${nickname}`);
      if (ip) {
        const ipWithPort = port && port !== '9100' ? `${ip}:${port}` : ip;
        await productionService.createConfigSetting(`${key}_ip`, ipWithPort, `Static IP for ${key}`);
      }
      // Assign to station (skip for virtual locations like supervisor_office)
      if (typeof stationId === 'number') {
        await stationManagementService.updateStationPrinter(stationId, key);
      }
    },
    {
      onSuccess: () => {
        toast.success(t('admin.printerAdded'));
        queryClient.invalidateQueries('registered-printers');
        queryClient.invalidateQueries('station-printers');
        queryClient.invalidateQueries('supervisor-stations');
        setShowAddForm(false);
        setNewNickname('');
        setNewIp('');
        setNewPort('9100');
        setNewMac('');
        setNewStationId('');
        setShowMacField(false);
      },
      onError: () => { toast.error('Failed to add printer'); },
    }
  );

  // Delete printer: removes main key + _ip key + unassigns station
  const deleteMutation = useMutation(
    async (printer: PrinterEntry) => {
      // Unassign station first (only for real numeric station ids; skip virtual locations)
      if (typeof printer.stationId === 'number') {
        await stationManagementService.updateStationPrinter(printer.stationId, null);
      }
      await productionService.deleteConfigSetting(printer.key);
      try { await productionService.deleteConfigSetting(`${printer.key}_ip`); } catch {}
    },
    {
      onSuccess: () => {
        toast.success(t('admin.printerDeleted'));
        queryClient.invalidateQueries('registered-printers');
        queryClient.invalidateQueries('station-printers');
        queryClient.invalidateQueries('supervisor-stations');
      },
      onError: () => { toast.error('Failed to remove printer'); },
    }
  );

  // Test print: sends a test label directly to the printer IP
  const testPrintMutation = useMutation(
    async (printer: PrinterEntry) => {
      setTestingKey(printer.key);
      const base = getPrintServiceUrl();
      const url = base ? `${base}/api/print/test` : '/api/print/test';
      const token = localStorage.getItem('authToken');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ printer_ip: printer.ip }),
      });
      if (!res.ok) throw new Error('Request failed');
      return res.json();
    },
    {
      onSuccess: (data) => {
        setTestingKey(null);
        if (data.success) {
          toast.success(t('stations.testPrintSuccess'));
        } else {
          toast.error(`${t('stations.testPrintFailed')}: ${data.error || ''}`);
        }
      },
      onError: () => {
        setTestingKey(null);
        toast.error(t('stations.testPrintFailed'));
      },
    }
  );

  const handleAdd = () => {
    if (!newNickname.trim() || !newIp.trim() || !newStationId) return;
    if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(newIp.trim())) {
      toast.error(t('admin.invalidIpAddress'));
      return;
    }
    if (newMac && !/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(newMac)) {
      toast.error('Invalid MAC format. Use XX:XX:XX:XX:XX:XX');
      return;
    }
    addMutation.mutate({
      nickname: newStationId === 'supervisor_office' ? 'supervisor_office' : newNickname.trim().replace(/\s+/g, '_').toLowerCase(),
      ip: newIp.trim(),
      port: newPort.trim() || '9100',
      mac: newMac.trim(),
      stationId: newStationId === 'supervisor_office' ? newStationId : Number(newStationId),
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Printer className="w-5 h-5 text-secondary-500" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white">{t('admin.registeredPrinters')}</h3>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('admin.addPrinter')}
        </button>
      </div>
      <p className="text-xs text-secondary-500 dark:text-gray-400 mb-4">{t('admin.registeredPrintersDesc')}</p>

      {/* Add Printer Form — matches scale form layout */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.printerNickname')}</label>
              <input
                type="text"
                value={newNickname}
                onChange={(e) => setNewNickname(e.target.value.replace(/\s+/g, '_').toLowerCase())}
                placeholder={t('admin.printerNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.printerIpAddress')}</label>
              <input
                type="text"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder={t('admin.printerStaticIpPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.printerPort')}</label>
              <input
                type="number"
                value={newPort}
                onChange={(e) => setNewPort(e.target.value)}
                placeholder="9100"
                min="1"
                max="65535"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.printerStation')}</label>
              <select
                value={newStationId}
                onChange={(e) => setNewStationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">{t('admin.selectStation')}</option>
                <option value="supervisor_office" disabled={supervisorOfficeAssigned}>
                  {t('admin.supervisorOffice')}{supervisorOfficeAssigned ? ` (${t('admin.assignedElsewhere')})` : ''}
                </option>
                {stations.map((s) => (
                  <option
                    key={s.station_id}
                    value={s.station_id}
                    disabled={assignedStationIds.has(s.station_id)}
                  >
                    {s.station_name}{assignedStationIds.has(s.station_id) ? ` (${t('admin.assignedElsewhere')})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleAdd}
                disabled={addMutation.isLoading || !newNickname.trim() || !newIp.trim() || !newStationId}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {addMutation.isLoading ? '...' : t('common.save')}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewNickname(''); setNewIp(''); setNewPort('9100'); setNewMac(''); setNewStationId(''); setShowMacField(false); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
          {/* Optional MAC field toggle */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowMacField(!showMacField)}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {showMacField ? t('admin.hideMac') : t('admin.showMac')}
            </button>
            {showMacField && (
              <div className="mt-2 max-w-xs">
                <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.printerMacValue')} ({t('common.optional')})</label>
                <input
                  type="text"
                  value={newMac}
                  onChange={(e) => setNewMac(e.target.value.toUpperCase())}
                  placeholder="XX:XX:XX:XX:XX:XX"
                  maxLength={17}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Printer List — card layout matching scales */}
      {isLoading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : printers.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-4">{t('admin.noPrintersRegistered')}</div>
      ) : (
        <div className="border rounded-lg divide-y dark:border-gray-700 dark:divide-gray-700">
          {printers.map((printer) => {
            const isTesting = testingKey === printer.key;
            return (
              <div key={printer.key} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Printer className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-secondary-900 dark:text-white">{printer.nickname}</div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {printer.stationName ? (
                          <span className="text-xs text-indigo-600 dark:text-indigo-400">{printer.stationName}</span>
                        ) : !printer.stationId && (
                          <span className="text-xs text-indigo-600 dark:text-indigo-400">{t('admin.supervisorOffice')}</span>
                        )}
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                          {printer.ip ? (printer.ip.includes(':') ? printer.ip : `${printer.ip}:9100`) : t('admin.noIpSet')}
                        </span>
                        {printer.mac && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">MAC: {printer.mac}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Test Print button */}
                    {printer.ip && (
                      <button
                        onClick={() => testPrintMutation.mutate(printer)}
                        disabled={isTesting}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {isTesting ? '...' : t('stations.testPrint')}
                      </button>
                    )}
                    {/* Delete button */}
                    <button
                      onClick={() => {
                        if (window.confirm(t('admin.confirmDeletePrinter'))) {
                          deleteMutation.mutate(printer);
                        }
                      }}
                      disabled={deleteMutation.isLoading}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RegisteredPrintersSection;
